import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { usePage, type SectionProps } from "@/flash/context";
import { A, arr, cn, str } from "@/flash/ui";
import { Shell } from "./kit";

/** Minimal floating header: wordmark left, three links centred, one outlined CTA right */
export default function SysNavbar({ brand, links, cta }: SectionProps) {
  const { ctaHref, ui } = usePage();
  const [open, setOpen] = useState(false);
  const [stuck, setStuck] = useState(false);
  const items = arr(links).slice(0, 4);
  const name = str(brand);
  const { pathname } = useLocation();
  const current = (href: unknown) => (str(href) === pathname ? "page" : undefined);

  // close the mobile menu on route change
  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const on = () => setStuck(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header className={cn("sx-nav fixed inset-x-0 top-0 z-50", (stuck || open) && "is-stuck")}>
      <Shell className="grid h-16 grid-cols-[1fr_auto] items-center md:h-[76px] md:grid-cols-[1fr_auto_1fr]">
        <A to="/" className="font-display w-fit whitespace-nowrap text-[17px] font-medium tracking-[-0.02em] text-foreground">
          {name}
        </A>
        <nav className="hidden items-center gap-10 md:flex" aria-label="Primary">
          {items.map((l, i) => (
            <A key={i} to={l.href} aria-current={current(l.href)} className="sx-navlink whitespace-nowrap text-[14px] text-foreground/65 transition-colors hover:text-foreground">
              {str(l.label)}
            </A>
          ))}
        </nav>
        <div className="flex items-center justify-end gap-2">
          {cta ? (
            <A to={ctaHref} className="sx-btn-outline hidden h-9 px-4 text-[13.5px] sm:inline-flex">
              {str(cta)}
            </A>
          ) : null}
          <button
            type="button"
            className="grid size-10 cursor-pointer place-items-center text-foreground/80 md:hidden"
            aria-expanded={open}
            aria-label={str(ui.menu) || "Menu"}
            onClick={() => setOpen(!open)}
          >
            {open ? <X className="size-5" strokeWidth={1.5} /> : <Menu className="size-5" strokeWidth={1.5} />}
          </button>
        </div>
      </Shell>
      {open ? (
        <div className="border-t border-white/[.06] md:hidden">
          <Shell className="flex flex-col py-4">
            {items.map((l, i) => (
              <A key={i} to={l.href} aria-current={current(l.href)} onClick={() => setOpen(false)} className="font-display border-b border-white/[.06] py-4 text-2xl tracking-tight text-foreground">
                {str(l.label)}
              </A>
            ))}
            {cta ? (
              <A to={ctaHref} onClick={() => setOpen(false)} className="sx-btn-outline mt-6 h-11 w-full justify-center text-[15px]">
                {str(cta)}
              </A>
            ) : null}
          </Shell>
        </div>
      ) : null}
    </header>
  );
}